import { MenuItem, Text } from "@chakra-ui/react";
import _ from "lodash";
import React from "react";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { asyncForEach } from "src/services/asyncHelpers";
import { HasuraRole } from "src/types/hasuraRole";
import { SchoolFormId } from "../types";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

const EXECUTE_FORM_RULES_MAX_INPUT_SIZE = 500;

interface Props {
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  isBulkAction?: boolean;
}

const CONFIRMATION_BODY_TEXT =
  "This will execute form rules for all selected forms. Form rules will automatically tag forms based on configured conditions.";

export const ExecuteFormRules = ({
  selectedFormSchools,
  onRefetch,
  isBulkAction = false,
}: Props) => {
  const toast = useAvelaToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, execute",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = React.useCallback(
    async (evt: React.MouseEvent) => {
      const formIds = _.uniq(selectedFormSchools.map((fs) => fs.formId));

      if (formIds.length === 0) {
        toast.error({
          title: "No forms selected",
          description: "Please select at least one form.",
        });
        return;
      }

      const count = formIds.length;
      const trackArgs = {
        actionType: "EXECUTE_FORM_RULES" as const,
        bulk: isBulkAction,
        count,
        async: false,
      };

      setHeader(
        <Text>{`Execute form rules for ${count} form${
          count !== 1 ? "s" : ""
        }?`}</Text>
      );
      setBody(<Text>{CONFIRMATION_BODY_TEXT}</Text>);

      trackFormAction({ verb: "Clicked", ...trackArgs });

      if (await confirm()) {
        trackFormAction({ verb: "Confirmed", ...trackArgs });

        const loadingToastId = toast({
          title: `Working hard`,
          description: `Executing form rules for ${count} form${
            count !== 1 ? "s" : ""
          }...`,
          status: "loading",
          duration: null,
        });

        setIsLoading(true);

        try {
          await asyncForEach(
            _.chunk(formIds, EXECUTE_FORM_RULES_MAX_INPUT_SIZE),
            async (formIdsChunk) => {
              await FSR.createRuleExecutions({
                body: {
                  data: {
                    targets: formIdsChunk.map((formId) => ({
                      type: "form" as const,
                      formId,
                    })),
                  },
                },
              });
            }
          );

          if (onRefetch) onRefetch();

          toast.close(loadingToastId);
          toast({
            title: "Success",
            description: `Form rules executed for ${count} form${
              count !== 1 ? "s" : ""
            }`,
            status: "success",
          });
          trackFormAction({ verb: "Completed", ...trackArgs });
        } catch (err: any) {
          trackFormActionError(err, trackArgs);
          toast.close(loadingToastId);
          toast.error({
            title: "Something went wrong!",
            description: err?.message || "Check your network and try again.",
          });
          console.error("Error executing form rules:", err);
        } finally {
          setIsLoading(false);
        }
      } else {
        trackFormAction({ verb: "Canceled", ...trackArgs });
      }
    },
    [
      confirm,
      isBulkAction,
      selectedFormSchools,
      onRefetch,
      setBody,
      setHeader,
      toast,
    ]
  );

  return (
    <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={isLoading || selectedFormSchools.length === 0}
      >
        Execute form rules
      </MenuItem>
      {confirmationDialog}
    </WithRequiredHasuraRoles>
  );
};
